import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { MuiDateRangePicker } from "../../framework/muiDateRangePicker";
import MuiSelect from "../../framework/muiSelect";
import {
    AutocompleteOption,
    AutocompleteWrapper,
} from "../../framework/muiAutocomplete";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    setDateRange,
    setRelatedObjectType,
    setRelatedObjectIds,
    setFormTemplateIds,
    setAnsweredByEmployeeIds,
    resetFilters,
} from "../../../store/formSummarySlice";
import { useAsyncThunkAction } from "../../../hooks/useAsyncThunkAction";
import { useReportOptionOptions } from "../../../hooks/reporting/useReportOptions";
import {
    fetchReportEmployees,
    fetchReportVehicles,
} from "../../../store/reportOptionsSlice";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { useFetch } from "../../../hooks/useFetch";
import * as formTemplateService from "../../../services/formTemplateService";
import { FormTemplateDto } from "../../../models/formTemplate/formTemplateDto";

export const FormSummaryFilters = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        dateRange,
        relatedObjectType,
        relatedObjectIds,
        formTemplateIds,
        answeredByEmployeeIds,
    } = useAppSelector((state) => state.formSummary);

    useAsyncThunkAction(() => fetchReportVehicles(), {}, []);
    useAsyncThunkAction(() => fetchReportEmployees(), {}, []);

    const handleDateRangeChange = useCallback(
        (value: [string, string]) => dispatch(setDateRange(value)),
        [dispatch]
    );

    const handleFormTemplateIdsChange = useCallback(
        (val: string[]) => dispatch(setFormTemplateIds(val)),
        [dispatch]
    );

    const handleRelatedObjectTypeChange = useCallback(
        (val: string) => dispatch(setRelatedObjectType(val)),
        [dispatch]
    );

    const handleRelatedObjectIdsChange = useCallback(
        (val: string[]) => dispatch(setRelatedObjectIds(val)),
        [dispatch]
    );

    const handleAnsweredByChange = useCallback(
        (val: string[]) => dispatch(setAnsweredByEmployeeIds(val)),
        [dispatch]
    );

    const clearFilters = useCallback(() => {
        dispatch(resetFilters());
    }, [dispatch]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (formTemplateIds.length > 0) count++;
        if (relatedObjectType) count++;
        if (answeredByEmployeeIds.length > 0) count++;
        return count;
    }, [formTemplateIds, relatedObjectType, answeredByEmployeeIds]);

    const targetTypeOptions = useMemo(
        () => [
            {
                label: t("settings.formEditor.targetObjectTypeVehicle"),
                value: "Vehicle",
            },
        ],
        [t]
    );

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <MuiDateRangePicker
                label={t("reporting.formSummary.answeredAt")}
                value={dateRange}
                onChange={handleDateRangeChange}
                size="small"
            />
            <Box sx={{ minWidth: 250 }}>
                <FormTemplateFilter
                    value={formTemplateIds}
                    onChange={handleFormTemplateIdsChange}
                />
            </Box>
            <Box sx={{ minWidth: 180 }}>
                <MuiSelect
                    label={t("settings.formEditor.targetObjectType")}
                    options={targetTypeOptions}
                    value={relatedObjectType}
                    onChange={handleRelatedObjectTypeChange}
                    size="small"
                    noSelectionLabel={t(
                        "settings.formEditor.targetObjectTypeNone"
                    )}
                    enableClearButton
                />
            </Box>
            <Box sx={{ minWidth: 250 }}>
                <VehicleTargetFilter
                    value={relatedObjectIds}
                    onChange={handleRelatedObjectIdsChange}
                    disabled={relatedObjectType !== "Vehicle"}
                />
            </Box>
            <Box sx={{ minWidth: 250 }}>
                <AnsweredByFilter
                    value={answeredByEmployeeIds}
                    onChange={handleAnsweredByChange}
                />
            </Box>
            <Button
                size="small"
                variant="text"
                startIcon={<FilterListOffIcon />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                sx={{ ml: "auto", whiteSpace: "nowrap" }}
                data-testid="form-summary-clear-filters"
            >
                {t("common.clear")}
                {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>
        </Box>
    );
};

const FormTemplateFilter = ({
    value,
    onChange,
}: {
    value: string[];
    onChange: (val: string[]) => void;
}) => {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState<FormTemplateDto[]>([]);

    useFetch(
        (signal) => formTemplateService.getFormTemplates(false, signal),
        { onSuccess: (response) => setTemplates(response.data) },
        []
    );

    const options: AutocompleteOption[] = useMemo(
        () => templates.map((tmpl) => ({ id: tmpl.id, title: tmpl.name })),
        [templates]
    );

    return (
        <AutocompleteWrapper
            label={t("reporting.formSummary.formName")}
            options={options}
            value={value}
            multiple
            onChange={(val) => {
                if (val === null) {
                    onChange([]);
                } else if (Array.isArray(val)) {
                    onChange(val.map((o) => o.id));
                }
            }}
            size="small"
            limitTags={2}
        />
    );
};

const AnsweredByFilter = ({
    value,
    onChange,
}: {
    value: string[];
    onChange: (val: string[]) => void;
}) => {
    const { t } = useTranslation();
    const employees = useAppSelector((state) => state.reportOptions.employees);

    const options: AutocompleteOption[] = useMemo(
        () => (employees ?? []).map((e) => ({ id: e.id, title: e.fullName })),
        [employees]
    );

    return (
        <AutocompleteWrapper
            label={t("reporting.formSummary.answeredBy")}
            options={options}
            value={value}
            multiple
            onChange={(val) => {
                if (val === null) {
                    onChange([]);
                } else if (Array.isArray(val)) {
                    onChange(val.map((o) => o.id));
                }
            }}
            size="small"
            limitTags={2}
        />
    );
};

const VehicleTargetFilter = ({
    value,
    onChange,
    disabled,
}: {
    value: string[];
    onChange: (val: string[]) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const [vehicleOptions, selectedNames, getIdsFromOptions] =
        useReportOptionOptions<VehicleListItemDto>(
            "vehicle",
            value,
            "id",
            "registerNumber"
        );

    return (
        <AutocompleteWrapper
            label={t("reporting.formSummary.target")}
            options={vehicleOptions}
            value={selectedNames}
            multiple
            disabled={disabled}
            onChange={(val) => {
                if (val === null) {
                    onChange([]);
                } else if (Array.isArray(val)) {
                    onChange(getIdsFromOptions(val));
                }
            }}
            size="small"
            limitTags={2}
        />
    );
};
